import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiHook } from "../../hooks/apiHook";
import WithdrawalReportSkeleton from "./WithdrawalReportSkeleton";

const WithdrawalReport = () => {
  const { t } = useTranslation();

  // UI input states (not immediately triggering fetch)
  const [statusInput, setStatusInput] = useState("all");
  const [startDateInput, setStartDateInput] = useState("");
  const [endDateInput, setEndDateInput] = useState("");
  // Applied (effective) filter states used for querying
  const [appliedStatus, setAppliedStatus] = useState("all");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  // API call for withdrawal report with filters
  const {
    data: withdrawalData,
    isLoading,
    error,
  } = ApiHook.CallWithdrawalReport(
    page,
    perPage,
    appliedStatus === "all" ? "" : appliedStatus,
    appliedStartDate,
    appliedEndDate
  );

  // Normalize server response shape
  const withdrawals = withdrawalData?.data || [];
  const totalPages = withdrawalData?.totalPages || 1;
  const totalRecords = withdrawalData?.total || withdrawals.length;
  const currentPage = withdrawalData?.currentPage || page;

  const handleApplyFilters = () => {
    setAppliedStatus(statusInput);
    setAppliedStartDate(startDateInput);
    setAppliedEndDate(endDateInput);
    setPage(1);
  };

  const handleResetFilters = () => {
    setStatusInput("all");
    setStartDateInput("");
    setEndDateInput("");
    setAppliedStatus("all");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setPerPage(10);
    setPage(1);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Released: { class: "badge-success", text: "Released", icon: "✓" },
      Paid: { class: "badge-success", text: "Paid", icon: "✓" },
      Completed: { class: "badge-success", text: "Completed", icon: "✓" },
      Requested: { class: "badge-warning", text: "Requested", icon: "⏳" },
      Pending: { class: "badge-warning", text: "Pending", icon: "⏳" },
      Approved: { class: "badge-info", text: "Approved", icon: "🔄" },
      Processing: { class: "badge-info", text: "Processing", icon: "🔄" },
      Rejected: { class: "badge-danger", text: "Rejected", icon: "✗" },
      Failed: { class: "badge-danger", text: "Failed", icon: "✗" },
      
      Cancelled: { class: "badge-secondary", text: "Cancelled", icon: "⚠" },
    };

    const config = statusConfig[status] || { 
      class: "badge-warning", 
      text: status || "Pending", 
      icon: "⏳" 
    };

    return (
      <span className={`status-badge ${config.class}`}>
        <span className="status-icon">{config.icon}</span>
        {config.text}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      console.log("Copied to clipboard:", text);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  if (isLoading) {
    return <WithdrawalReportSkeleton />;
  }

  if (error) {
    return (
      <div className="error-state">
        <div className="error-content">
          <i className="fas fa-exclamation-triangle"></i>
          <h5>Error Loading Data</h5>
          <p>Unable to load withdrawal report. Please try again.</p>
          <button
            className="btn-retry"
            onClick={() => window.location.reload()}
          >
            <i className="fas fa-refresh"></i>
            Retry
          </button>
        </div>
      </div>
    );
  }



  return (
    <div className="withdrawal-report">
      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Status</label>
            <select
              value={statusInput}
              onChange={(e) => setStatusInput(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="filter-group date-range">
            <label>Date Range</label>
            <div className="date-inputs">
              <input
                type="date"
                value={startDateInput}
                onChange={(e) => setStartDateInput(e.target.value)}
                className="date-input"
              />
              <span className="date-separator">to</span>
              <input
                type="date"
                value={endDateInput}
                onChange={(e) => setEndDateInput(e.target.value)}
                className="date-input"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn-filter" onClick={handleApplyFilters}>
              <i className="fas fa-search"></i>
              Apply
            </button>
            <button className="btn-reset" onClick={handleResetFilters}>
              <i className="fas fa-refresh"></i>
            </button>
            <div className="d-flex align-items-center ms-2">
              <select
                className="filter-select"
                style={{ width: 90 }}
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}/page
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Withdrawal Table */}

      {!withdrawals || withdrawals.length === 0 ? (
        <>
          <div className="empty-state">
            <div className="empty-content">
              <div className="empty-icon">
                <i className="fas fa-arrow-down"></i>
              </div>
              <h4>No Withdrawal History</h4>
              <p>You haven't made any GGFC token withdrawals yet.</p>
              {/* <button className="btn-primary">
            <i className="fas fa-plus"></i>
            Make First Withdrawal
          </button> */}
            </div>
          </div>
        </>
      ) : (
        <>
          {" "}
          <div className="table-container">
            <div className="table-wrapper">
              <table className="withdrawal-table">
                <thead>
                  <tr>
                    <th>SN</th>
                    <th>Date & Time</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Withdrawal Fee</th>
                    <th>Payment Method</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((withdrawal, index) => {
                    const dateInfo = formatDate(
                      withdrawal.updatedAt || withdrawal.createdAt
                    );
                    return (
                      <tr key={index} className="table-row">
                        <td>
                          <span className="withdrawal-id">
                            {(currentPage - 1) * perPage + index + 1}
                          </span>
                        </td>
                        <td>
                          <div className="date-column">
                            <div className="date">{dateInfo.date}</div>
                            <div className="time">{dateInfo.time}</div>
                          </div>
                        </td>
                        <td>
                          <div className="amount-column">
                            <div className="amount">
                              {formatCurrency(withdrawal.amount)} GGFC
                            </div>
                          </div>
                        </td>
                        <td>{getStatusBadge(withdrawal.status)}</td>
                        <td>{formatCurrency(withdrawal.payoutFee)} GGFC</td>
                        <td>
                          {withdrawal.paymentMethod ? (
                            <div className="hash-column">
                              <span className="hash-text">
                                {withdrawal.paymentMethod.toUpperCase()}
                              </span>
                            </div>
                          ) : (
                            <span className="pending-hash">Pending...</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Pagination */}
      <div className="pagination-section">
        <div className="pagination-info">
          Page {currentPage} of {totalPages} | Showing {withdrawals.length} of{" "}
          {totalRecords} entries
        </div>
        <div className="pagination-controls">
          <button
            className="page-btn"
            disabled={currentPage === 1}
            onClick={() => setPage(1)}
            title="First"
          >
            <i className="fas fa-angle-double-left"></i>
          </button>
          <button
            className="page-btn"
            disabled={currentPage === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            title="Previous"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <span className="page-btn active">{currentPage}</span>
          <button
            className="page-btn"
            disabled={currentPage === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            title="Next"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
          <button
            className="page-btn"
            disabled={currentPage === totalPages}
            onClick={() => setPage(totalPages)}
            title="Last"
          >
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>

      <style jsx>{`
        .withdrawal-report {
          background: #fff;
          border-radius: 12px;
          // overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        /* Header */

        .btn-export {
          background: #954cea;
          border: 1px solid r#954cea;
          color: white;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .btn-export:hover {
          background: rgba(10, 10, 10, 0.56);
          transform: translateY(-1px);
        }

        /* Filters */
        .filters-section {
          padding: 20px 28px;
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
          margin-top: 57px;
        }

        .filters-grid {
          display: grid;
          grid-template-columns: 150px 350px 350px auto;
          gap: 20px;
          align-items: end;
        }

        .filter-group label {
          display: block;
          margin-bottom: 6px;
          font-size: 13px;
          font-weight: 500;
          color: #495057;
        }

        .filter-select,
        .date-input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #ced4da;
          border-radius: 6px;
          font-size: 14px;
          background: white;
          transition: border-color 0.2s ease;
        }

        .filter-select:focus,
        .date-input:focus {
          outline: none;
          border-color: #32009c;
          box-shadow: 0 0 0 3px rgba(50, 0, 156, 0.1);
        }

        .date-range .date-inputs {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .date-separator {
          font-size: 12px;
          color: #6c757d;
        }

        .filter-actions {
          display: flex;
          gap: 8px;
        }

        .btn-filter,
        .btn-reset {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .btn-filter {
          background: #32009c;
          color: white;
          border: 1px solid #32009c;
        }

        .btn-filter:hover {
          background: #2a0085;
          transform: translateY(-1px);
        }

        .btn-reset {
          background: #6c757d;
          color: white;
          border: 1px solid #6c757d;
          padding: 8px 12px;
        }

        .btn-reset:hover {
          background: #5a6268;
          transform: translateY(-1px);
        }

        /* Table */
        .table-container {
          overflow-x: auto;
        }

        .table-wrapper {
          min-width: 800px;
        }

        .withdrawal-table {
          width: 100%;
          border-collapse: collapse;
        }

        .withdrawal-table th {
          background: #f8f9fa;
          padding: 16px 20px;
          text-align: left;
          font-size: 13px;
          font-weight: 600;
          color: #495057;
          border-bottom: 2px solid #dee2e6;
        }

        .withdrawal-table td {
          padding: 16px 20px;
          border-bottom: 1px solid #e9ecef;
          vertical-align: middle;
        }

        .table-row:hover {
          background: #f8f9fa;
        }

        /* Table Content */
        .withdrawal-id {
          font-family: "Monaco", "Menlo", monospace;
          font-size: 13px;
          color: #32009c;
          font-weight: 600;
        }

        .date-column .date {
          font-size: 14px;
          color: #212529;
          font-weight: 500;
        }

        .date-column .time {
          font-size: 12px;
          color: #6c757d;
          margin-top: 2px;
        }

        .amount-column .amount {
          font-size: 14px;
          font-weight: 600;
          color: #212529;
        }

        .amount-column .fee {
          font-size: 12px;
          color: #dc3545;
          margin-top: 2px;
        }

        .amount-column .net-amount {
          font-size: 12px;
          color: #28a745;
          margin-top: 2px;
          font-weight: 500;
        }

        /* Status Badges */
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
        }

        .badge-success {
          background: #d4edda;
          color: #155724;
        }

        .badge-warning {
          background: #fff3cd;
          color: #856404;
        }

        .badge-info {
          background: #d1ecf1;
          color: #0c5460;
        }

        .badge-danger {
          background: #f8d7da;
          color: #721c24;
        }

        .badge-secondary {
          background: #e2e3e5;
          color: #383d41;
        }

        .status-icon {
          font-size: 10px;
        }

        .processing-time {
          font-size: 13px;
          color: #6c757d;
        }

        /* Hash Column */
        .hash-column {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .hash-text {
          font-family: "Monaco", "Menlo", monospace;
          font-size: 12px;
          color: #495057;
        }

        .copy-btn {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          color: #6c757d;
          padding: 4px 6px;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 10px;
        }

        .copy-btn:hover {
          background: #e9ecef;
          color: #495057;
        }

        .pending-hash {
          font-size: 12px;
          color: #6c757d;
          font-style: italic;
        }

        /* Action Buttons */
        .action-buttons {
          display: flex;
          gap: 6px;
        }

        .action-btn {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          color: #6c757d;
          padding: 6px 8px;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 12px;
        }

        .view-btn:hover {
          background: #e7f3ff;
          color: #0066cc;
          border-color: #b3d9ff;
        }

        .cancel-btn:hover {
          background: #ffe6e6;
          color: #cc0000;
          border-color: #ffb3b3;
        }

        /* Pagination */
        .pagination-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 28px;
          background: #f8f9fa;
          border-top: 1px solid #e9ecef;
        }

        .pagination-info {
          font-size: 13px;
          color: #6c757d;
        }

        .pagination-controls {
          display: flex;
          gap: 4px;
        }

        .page-btn {
          background: white;
          border: 1px solid #dee2e6;
          color: #495057;
          padding: 6px 10px;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 12px;
          min-width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .page-btn:hover:not(:disabled) {
          background: #e9ecef;
        }

        .page-btn.active {
          background: #32009c;
          color: white;
          border-color: #32009c;
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Empty State */
        .empty-state {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          background: white;
          border-radius: 12px;
        }

        .empty-content {
          text-align: center;
          max-width: 400px;
        }

        .empty-icon {
          width: 80px;
          height: 80px;
          margin: 0 auto 20px;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          color: #dee2e6;
        }

        .empty-content h4 {
          margin: 0 0 12px 0;
          color: #495057;
          font-size: 18px;
        }

        .empty-content p {
          margin: 0 0 24px 0;
          color: #6c757d;
          line-height: 1.5;
        }

        .btn-primary {
          background: #32009c;
          color: white;
          border: 1px solid #32009c;
          padding: 12px 24px;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .btn-primary:hover {
          background: #2a0085;
          transform: translateY(-1px);
        }

        /* Error State */
        .error-state {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          background: white;
          border-radius: 12px;
        }

        .error-content {
          text-align: center;
          max-width: 400px;
        }

        .error-content i {
          font-size: 48px;
          color: #dc3545;
          margin-bottom: 16px;
        }

        .error-content h5 {
          margin: 0 0 8px 0;
          color: #495057;
        }

        .error-content p {
          margin: 0 0 20px 0;
          color: #6c757d;
        }

        .btn-retry {
          background: #dc3545;
          color: white;
          border: 1px solid #dc3545;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .btn-retry:hover {
          background: #c82333;
          transform: translateY(-1px);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .filters-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .report-header {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }

          .withdrawal-table th,
          .withdrawal-table td {
            padding: 12px 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default WithdrawalReport;
